import React, { createContext, useContext, forwardRef } from 'react';
import { Dialog, DialogTitle, Popover, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';

const GGDialogOnCloseContext = createContext(undefined);

const StyledConfirmDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    border: `2px solid ${theme.palette.background.dark}`,
    position: 'relative',
    backgroundColor: theme.palette.background.light,
  },
}));

const StyledGGDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'fullHeight',
})(({ theme, fullHeight }) => ({
  '& .MuiDialog-paper': {
    border: `2px solid ${theme.palette.background.dark}`,
    position: 'relative',
    backgroundColor: theme.palette.background.light,
    ...(fullHeight && {
      minHeight: '80vh',
    }),
  },
}));

const StyledGGDialogTitle = styled(DialogTitle)(({ theme }) => ({
  fontFamily: 'MedievalSharp',
  fontSize: '1.5rem',
  fontWeight: 600,
  backgroundColor: theme.palette.background.light,
  color: theme.palette.text.primary,
  borderBottom: `2px solid ${theme.palette.background.dark}`,
}));

export const ConfirmDialog = forwardRef(function ConfirmDialog({ onClose, children, ...props }, ref) {
  return (
    <GGDialogOnCloseContext.Provider value={onClose}>
      <StyledConfirmDialog ref={ref} onClose={onClose} {...props}>
        {children}
      </StyledConfirmDialog>
    </GGDialogOnCloseContext.Provider>
  );
});
ConfirmDialog.displayName = 'ConfirmDialog';

export const GGDialog = forwardRef(function GGDialog({ onClose, children, ...props }, ref) {
  return (
    <GGDialogOnCloseContext.Provider value={onClose}>
      <StyledGGDialog ref={ref} onClose={onClose} {...props}>
        {children}
      </StyledGGDialog>
    </GGDialogOnCloseContext.Provider>
  );
});
GGDialog.displayName = 'GGDialog';

export function GGDialogTitle({ children, sx, ...other }) {
  const onClose = useContext(GGDialogOnCloseContext);

  return (
    <StyledGGDialogTitle
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        pr: 1,
        ...sx,
      }}
      {...other}
    >
      <Box sx={{ flex: 1, minWidth: 0, alignSelf: 'center' }}>{children}</Box>
      {typeof onClose === 'function' ? (
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{
            flexShrink: 0,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </StyledGGDialogTitle>
  );
}

export const GGPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPopover-paper': {
    border: `2px solid ${theme.palette.background.dark}`,
    backgroundColor: theme.palette.background.light,
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
  },
}));
